// Case 3 of the access restrictions (ClickUp 86akmywy6): the workspace is on
// trial or active but has no ad account, so every card keeps its place and
// title and shows one empty state instead of data. No dashboard query runs.
import {
	CheckVerified01Solid,
	Globe06Solid,
	LayoutAlt01Solid,
	ShieldDollarSolid,
	ShieldTickSolid,
	ShieldZapSolid,
	StatCard,
	Typography,
	useEnterExit
} from '@clickguard/design-system'
import Dataflow02 from '@untitled-ui/icons-react/build/esm/Dataflow02'
import type { ComponentType, CSSProperties } from 'react'

const EMPTY_COPY = 'Connect an ad account to enable the dashboard'

const ENTER_MS = 200

export type CardDef = {
	title: string
	Icon: ComponentType<{ className?: string; style?: CSSProperties }>
	/** The real card's icon colour (New sources blocked is the only dark one). */
	color?: string
}

// Same titles, icons, column ratios and breakpoint (xl) as the live cards in
// view.tsx, row by row. Stacked cards drop to one short height, since they
// only hold one line.
export const DASHBOARD_ROWS: Array<{
	grid: string
	height: string
	cards: CardDef[]
}> = [
	{
		grid: 'xl:grid-cols-[640fr_494fr]',
		height: 'h-[180px] xl:h-[212px]',
		cards: [
			{ title: 'Traffic quality', Icon: ShieldTickSolid },
			{ title: 'Traffic Protection', Icon: ShieldDollarSolid }
		]
	},
	{
		grid: 'xl:grid-cols-[640fr_494fr]',
		height: 'h-[180px] xl:h-[360px]',
		cards: [
			{ title: 'Protection analysis', Icon: ShieldZapSolid },
			{
				title: 'New sources blocked',
				Icon: CheckVerified01Solid,
				color: 'var(--t-dark-blue)'
			}
		]
	},
	{
		grid: 'xl:grid-cols-[501fr_633fr]',
		height: 'h-[180px] xl:h-[460px]',
		cards: [
			{ title: 'Traffic quality breakdown by Geography', Icon: Globe06Solid },
			{ title: 'Traffic quality breakdown by Channel', Icon: LayoutAlt01Solid }
		]
	}
]

const EmptyCard = ({
	title,
	Icon,
	color,
	height
}: CardDef & { height: string }) => (
	<StatCard
		size="small"
		title={title}
		icon={
			<Icon
				className="w-4 h-4 flex-shrink-0"
				style={{ color: color ?? 'var(--color-brand)' }}
			/>
		}
		className={`w-full ${height}`}
	>
		<div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
			<Dataflow02 width={24} height={24} className="text-t-secondary" />
			<Typography variant="body-sm" color="muted" align="center">
				{EMPTY_COPY}
			</Typography>
		</div>
	</StatCard>
)

export const NoAdAccountDashboard = () => {
	const { entered } = useEnterExit(true, { duration: ENTER_MS })

	return (
		<div
			className={`flex flex-col gap-3 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
				entered ? 'opacity-100' : 'opacity-0 translate-y-1'
			}`}
		>
			{DASHBOARD_ROWS.map((row) => (
				<div
					key={row.cards[0].title}
					className={`grid grid-cols-1 ${row.grid} gap-3`}
				>
					{row.cards.map((card) => (
						<EmptyCard key={card.title} {...card} height={row.height} />
					))}
				</div>
			))}
		</div>
	)
}
